import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  LOCALE_ID,
  OnInit,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { auditTime, distinctUntilChanged, take } from "rxjs/operators";
import { Subject } from "rxjs";

import { DatePipe, SlicePipe } from "@angular/common";
import { DownloadFilesService } from "../../../data/media/download-files.service";
import { RecordData } from "../../../types";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import {
  UserRecordListState,
  UserRecordListViewModel,
} from "./user-record-list.viewmodel";
import { UnifiedRow } from "./unified-row.model";
import { COMMON_I18N } from '@app/i18n';
import {
  getRecordTypePrimaryLabel,
  getRecordTypeSecondaryLabel,
} from "./record-type-display.util";

@Component({
  selector: "app-user-record-list",
  templateUrl: "./user-record-list.component.html",
  styleUrls: ["./user-record-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: flex; flex-direction: column; flex: 1; min-height: 0;',
  },
  imports: [...SHARED_IMPORTS, DatePipe, SlicePipe],
  providers: [UserRecordListViewModel],
})
export class UserRecordListComponent implements OnInit, AfterViewInit {
  readonly scrollContainer = viewChild<ElementRef<HTMLElement>>("scrollContainer");
  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>("searchInput");

  filterText = "";
  isLoadingRecordList = false;
  readonly searchExpanded = signal(false);
  private pendingScrollTop: number | null = null;
  private readonly scrollSubject = new Subject<number>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly paginatorIntl = inject(MatPaginatorIntl);
  private readonly downloadFilesService = inject(DownloadFilesService);
  private readonly vm = inject(UserRecordListViewModel);
  private readonly localeId = inject(LOCALE_ID);
  private readonly protocolLocale: "ja" | "en" | "zh-TW" = (() => {
    const lower = (this.localeId || "").toLowerCase();
    return lower.startsWith("en") ? "en" : lower.startsWith("zh") ? "zh-TW" : "ja";
  })();
  private readonly unavailableValue = "-";
  private readonly coughPresentLabel = $localize`:@@home.userRecordList.value.coughPresence.present:あり`;
  private readonly coughAbsentLabel = $localize`:@@home.userRecordList.value.coughPresence.absent:なし`;
  readonly vm$ = this.vm.vm$;

  ngOnInit(): void {
    this.configurePaginatorLabels();

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const userId = params.get("userId");
        if (userId) {
          this.vm.init(userId, this.resolveRecordTypeFilter());
        }
      });

    this.vm$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => this.applyState(state));

    this.scrollSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        auditTime(100),
        distinctUntilChanged(),
      )
      .subscribe((scrollTop) => this.vm.updateScrollPosition(scrollTop));
  }

  ngAfterViewInit(): void {
    // Scroll position is restored in applyState() after VM state updates
  }

  onFilterInput(event?: Event): void {
    if (event) {
      const target = event.target as HTMLInputElement | null;
      this.filterText = target?.value ?? "";
    }
    this.vm.updateFilter(this.filterText);
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput()?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filterText.trim()) {
      this.searchExpanded.set(false);
    }
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filterText = "";
    this.onFilterInput();
    this.searchInput()?.nativeElement?.focus();
  }

  onPaginatorChange(event: PageEvent): void {
    this.vm.updatePagination(event.pageIndex, event.pageSize);
  }

  onSortChange(sort: Sort): void {
    this.vm.updateSort(sort.active, sort.direction || "");
  }

  onProtocolFilterChange(recordType: string, selected: boolean): void {
    this.vm.toggleProtocolFilter(recordType, selected);
  }

  selectAllProtocols(): void {
    this.vm.selectAllProtocolFilters();
  }

  clearAllProtocols(): void {
    this.vm.clearAllProtocolFilters();
  }

  onTableScroll(): void {
    const el = this.scrollContainer()?.nativeElement;
    if (!el) {
      return;
    }
    this.scrollSubject.next(el.scrollTop);
  }

  navigateToRow(row: UnifiedRow): void {
    this.vm$.pipe(take(1)).subscribe((state) => {
      const link = this.getRowDetailLink(state, row);
      if (link) {
        const qp = this.getRowQueryParams(row);
        this.router.navigate(link, qp ? { queryParams: qp } : undefined);
      }
    });
  }

  protected getRowDetailLink(
    state: UserRecordListState,
    row: UnifiedRow,
  ): string[] | null {
    const sectionId = state.section?.sectionId;
    const userId = state.selectedUserWithUserData?.user?.userId;
    if (!sectionId || !userId || !row.id) {
      return null;
    }

    const base = ["/home", sectionId, "users", userId];
    if (row.kind === 'media') {
      return [...base, "medias", row.id];
    }
    return this.isGostContextRoute()
      ? [...base, "gost", "records", row.id]
      : [...base, "records", row.id];
  }

  /** Query params for row navigation (media rows get from=records) */
  protected getRowQueryParams(row: UnifiedRow): Record<string, string> | null {
    return row.kind === 'media' ? { from: 'records' } : null;
  }

  toggleColumnVisibility(columnDef: string): void {
    this.vm.toggleColumnVisibility(columnDef);
  }

  onRefresh(): void {
    this.vm.refresh();
  }

  onDownload(event: Event, row: UnifiedRow): void {
    event.stopPropagation();
    if (row.kind !== 'record' || !row.record) {
      return;
    }

    const userId = row.record.meta?.user?.userId;
    if (!userId) {
      return;
    }

    const postParams = {
      measurerId: "",
      userId,
      fileType: "",
      recordId: [row.id],
    };

    this.downloadFilesService.downloadFilesById(postParams).subscribe({
      next: (res) => {
        window.location.href = (res as { url: string }).url;
      },
      error: () => {
        console.error("[UserRecordListComponent] downloadFilesById failed");
      },
    });
  }

  protected getProtocolPrimaryLabel(row: UnifiedRow): string {
    if (row.kind === 'media') {
      return row.media?.data?.method ?? '';
    }
    return getRecordTypePrimaryLabel(
      row.record?.data?.recordType,
      row.record?.data?.recordTypeView,
      this.protocolLocale,
    );
  }

  protected getProtocolSecondaryLabel(row: UnifiedRow): string {
    if (row.kind === 'media') {
      return '';
    }
    if (this.protocolLocale === "en") {
      return "";
    }
    const primary = this.getProtocolPrimaryLabel(row);
    return getRecordTypeSecondaryLabel(row.record?.data?.recordType, primary);
  }

  protected getProtocolTooltip(row: UnifiedRow): string {
    const primary = this.getProtocolPrimaryLabel(row);
    const secondary = this.getProtocolSecondaryLabel(row);
    return secondary ? `${primary} (${secondary})` : primary;
  }

  protected isProtocolSelected(
    state: UserRecordListState,
    recordType: string,
  ): boolean {
    return state.selectedProtocolFilters.includes(recordType);
  }

  protected areAllProtocolsSelected(state: UserRecordListState): boolean {
    if (state.protocolFilterOptions.length === 0) {
      return false;
    }
    const selected = new Set(state.selectedProtocolFilters);
    return state.protocolFilterOptions.every((option) => selected.has(option.code));
  }

  protected areNoProtocolsSelected(state: UserRecordListState): boolean {
    const selected = new Set(state.selectedProtocolFilters);
    return !state.protocolFilterOptions.some((option) => selected.has(option.code));
  }

  protected formatMetricValue(value: unknown): string {
    if (value == null || value === "") {
      return this.unavailableValue;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? String(value)
      : this.unavailableValue;
  }

  protected formatMeanSwallowingTime(value: unknown): string {
    if (value == null || value === "") {
      return this.unavailableValue;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return this.unavailableValue;
    }
    const seconds = numericValue / 1000;
    return seconds.toFixed(1);
  }

  protected getCoughPresenceLabel(row: UnifiedRow): string {
    if (row.kind === 'media') {
      return this.unavailableValue;
    }
    const coughCount = row.record?.data?.numOfCoughs;
    if (coughCount == null || coughCount === "") {
      return this.unavailableValue;
    }
    const numericValue = Number(coughCount);
    if (!Number.isFinite(numericValue)) {
      return this.unavailableValue;
    }
    return numericValue >= 1
      ? this.coughPresentLabel
      : this.coughAbsentLabel;
  }

  private applyState(state: UserRecordListState): void {
    this.filterText = state.filterText;
    this.isLoadingRecordList = state.status !== "success";
    this.pendingScrollTop = state.scrollTop;
    this.restoreScrollPosition();
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return `0 / ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} - ${endIndex} / ${length}`;
    };
    this.paginatorIntl.changes.next();
  }

  private restoreScrollPosition(): void {
    const el = this.scrollContainer();
    if (!el) {
      return;
    }
    const target = this.pendingScrollTop ?? 0;
    el.nativeElement.scrollTop = target;
  }

  private isGostContextRoute(): boolean {
    return this.router.url.includes("/gost/");
  }

  private resolveRecordTypeFilter(): string[] | null {
    const raw = this.route.snapshot.data['recordTypeFilter'];
    if (!Array.isArray(raw)) {
      return null;
    }
    const normalized = raw
      .map((item) => (item == null ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
    return normalized.length > 0 ? normalized : null;
  }
}
